<template>
  <n-modal
    :show="show"
    @update:show="$emit('update:show', $event)"
    display-directive="show"
    preset="card"
    style="width: 98vw; max-width: 98vw"
    :bordered="false"
    size="huge"
    role="dialog"
    aria-modal="true"
    @after-leave="onClose"
  >
    <template #header>
      <div class="moodboard-modal-header">
        <n-input
          v-if="isEditingName"
          ref="nameInputRef"
          v-model:value="editNameValue"
          size="medium"
          placeholder="Board name"
          @blur="saveName"
          @keydown.enter.prevent="saveName"
          @keydown.escape.prevent="cancelEditName"
        />
        <span
          v-else
          class="moodboard-name-clickable"
          role="button"
          tabindex="0"
          @click="startEditName"
          @keydown.enter.prevent="startEditName"
          @keydown.space.prevent="startEditName"
        >{{ displayName }}</span>
      </div>
    </template>
    <div class="moodboard-board-modal">
      <div v-if="images.length === 0" class="empty-canvas">
        <n-empty description="No images in this moodboard" />
      </div>
      <div v-else-if="currentPageImages.length === 0" class="empty-canvas">
        <n-empty description="No images on this page" />
      </div>
      <div v-else-if="layoutRects.length === 0" class="loading-canvas">
        <n-spin size="large" />
        <span>Preparing layout...</span>
      </div>
      <template v-else>
        <div class="toolbar">
          <div class="toolbar-left">
            <n-button
              quaternary
              size="tiny"
              :disabled="totalPages >= MAX_PAGES || images.length === 0"
              :title="`Add page (${totalPages}/${MAX_PAGES})`"
              @click="onAddPage"
            >+ Page</n-button>
          </div>
          <div class="toolbar-right">
            <span class="aspect-label">Hierarchy</span>
            <n-slider
              v-model:value="emphasisLevel"
              :min="0"
              :max="1"
              :step="0.05"
              style="width: 80px"
              :tooltip="false"
              @update:value="computeLayout({ forceAuto: true })"
            />
            <span class="aspect-label">Aspect</span>
            <n-button-group size="tiny">
              <n-button
                quaternary
                size="tiny"
                :type="isHorizontal ? 'primary' : 'default'"
                @click="setBoardOrientation('horizontal')"
              >
                H
              </n-button>
              <n-button
                quaternary
                size="tiny"
                :type="isVertical ? 'primary' : 'default'"
                @click="setBoardOrientation('vertical')"
              >
                V
              </n-button>
            </n-button-group>
            <span class="aspect-note">{{ isHorizontal ? '7×5' : '5×7' }}</span>
            <n-button
              quaternary
              size="tiny"
              type="default"
              :loading="exportingPsd"
              :disabled="images.length === 0"
              @click="onExportPsd"
              title="Export as PSD"
            >
              Export
            </n-button>
          </div>
        </div>
        <div class="board-and-pages">
          <div class="canvas-wrap" ref="canvasWrapRef">
            <div class="canvas-inner" :style="{ width: canvasWidth + 'px', height: canvasHeight + 'px' }">
            <canvas
              ref="canvasRef"
              :width="canvasWidth"
              :height="canvasHeight"
              :class="{ 'is-dragging': isDragging }"
              @mousedown="onCanvasMouseDown"
              @mousemove="onCanvasMouseMove"
              @mouseup="onCanvasMouseUp"
              @wheel.prevent="onCanvasWheel"
            />
            <div
              v-if="selectedImageIndex !== null && selectedRect"
              class="image-action-overlay"
              :style="{
                left: selectedRect.left + 'px',
                top: Math.max(0, selectedRect.top - 36) + 'px',
              }"
            >
              <n-button quaternary size="tiny" @click="onDelete" title="Remove from moodboard">
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24" fill="currentColor"><path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/></svg></n-icon>
                </template>
                Delete
              </n-button>
              <n-button v-if="!selectedImageIsFontItem" quaternary size="tiny" @click="onStar" :type="selectedImageStarred ? 'primary' : 'default'" title="I like this (draw larger)">
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/></svg></n-icon>
                </template>
                Star
              </n-button>
              <n-button quaternary size="tiny" @click="onRefresh" title="Re-randomize with this as focal">
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"/></svg></n-icon>
                </template>
                Refresh
              </n-button>
              <n-button v-if="!selectedImageIsFontItem" quaternary size="tiny" @click="onCrop" title="Crop image">
                <template #icon>
                  <n-icon><svg viewBox="0 0 24 24" fill="currentColor"><path d="M17,15H19V19H15V21H19A2,2 0 0,0 21,19V15H23V13H21V7H15V5H19A2,2 0 0,0 21,3V5H23V7H21V13H19V15M7,5V3H5A2,2 0 0,0 3,5V7H5V19H17V21H19V19H21V17H7V5M5,17V7H15V17H5Z"/></svg></n-icon>
                </template>
                Crop
              </n-button>
              <div v-if="totalPages > 1 && !selectedImageIsFontItem" style="position: relative; display: inline-flex;">
                <n-button quaternary size="tiny" @click="onMovePageClick" title="Move to another page">
                  Move Page
                </n-button>
                <div v-if="showMovePagePopup && totalPages > 2" class="move-page-popup">
                  <template v-for="p in totalPages" :key="p">
                    <n-button
                      v-if="p !== activePage"
                      quaternary size="tiny"
                      @click="onMovePage(p)"
                    >Page {{ p }}</n-button>
                  </template>
                </div>
              </div>
            </div>
            </div>
          </div>
          <!-- Page thumbnail sidebar -->
          <div v-if="totalPages > 1" class="pages-sidebar">
            <div
              v-for="p in totalPages"
              :key="p"
              class="page-thumb"
              :class="{ active: p === activePage }"
              @click="activePage = p"
            >
              <canvas
                :ref="(el) => setThumbCanvasRef(p, el as HTMLCanvasElement | null)"
                :width="112"
                :height="80"
                class="page-thumb-canvas"
              />
              <span class="page-thumb-label">{{ p }}</span>
            </div>
          </div>
        </div>
      </template>
      <!-- Typefaces reference strip -->
      <div v-if="typefaceItems.length > 0" class="typefaces-strip">
        <span class="typefaces-label">Typefaces</span>
        <!-- Word specimen button / inline input -->
        <input
          v-if="isEditingTypeSample"
          ref="typeSampleInputRef"
          class="type-sample-input"
          v-model="typeSampleDraft"
          :maxlength="24"
          :disabled="generatingWordSpecimens"
          placeholder="e.g. KIPPERED"
          @keydown.enter.prevent="commitTypeSample"
          @keydown.escape.prevent="cancelTypeSample"
          @blur="cancelTypeSample"
        />
        <button
          v-else
          class="type-sample-btn"
          :class="{ loading: generatingWordSpecimens, active: typeSampleText !== 'Aa' }"
          :disabled="generatingWordSpecimens"
          :title="typeSampleText !== 'Aa' ? `Sample text: ${typeSampleText} — click to change` : 'Set sample text for type images'"
          @click="startEditTypeSample"
        >
          <span v-if="generatingWordSpecimens" class="type-sample-spinner" />
          <span v-else>{{ typeSampleText }}</span>
        </button>
        <button
          v-for="item in typefaceItems"
          :key="item.id"
          class="typeface-chip"
          @click="emit('fontSelected', item.fontId!)"
          :title="`Open ${item.fontName} in type library`"
        >
          {{ item.fontName }}
        </button>
      </div>
    </div>

    <!-- Crop modal -->
    <MoodboardCropModal
      v-model:show="showCropModal"
      :image-url="cropImageUrl"
      :image-width="cropImageWidth"
      :image-height="cropImageHeight"
      :initial-crop="cropInitialCrop"
      @confirm="onCropConfirm"
      @clear="onCropClear"
    />
  </n-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useMessage } from 'naive-ui'
import { computePackingLayout, rectsToPositionScale, type ImageForLayout, type LayoutRect } from '@/composables/useMoodboardLayouts'
import { apiClient } from '@/services/api'
import MoodboardCropModal from './MoodboardCropModal.vue'
import type { NormalizedCrop } from './MoodboardCropModal.vue'

export interface MoodboardBoardImage {
  id: string
  moodboardItemId: string
  itemData?: string
  url?: string
  previewUrl?: string
  iconUrl?: string
  width?: number
  height?: number
  starred?: boolean
  rating?: number | null
  order?: number
  positionX?: number | null
  positionY?: number | null
  scale?: number | null
  cropLeft?: number | null
  cropTop?: number | null
  cropWidth?: number | null
  cropHeight?: number | null
  pageNumber?: number
  isFontItem?: boolean
  fontId?: string
  fontName?: string
  woff2Url?: string | null
  word?: string
}

const props = withDefaults(
  defineProps<{
    show: boolean
    moodboardId: string
    moodboardName?: string
    images: MoodboardBoardImage[]
    canvasWidth?: number
    canvasHeight?: number
  }>(),
  {
    canvasWidth: 900,
    canvasHeight: 600
  }
)

const emit = defineEmits<{
  'update:show': [value: boolean]
  closed: []
  deleted: [itemId: string]
  layoutSaved: []
  cropUpdated: [payload: { moodboardItemId: string; crop: NormalizedCrop | null }]
  nameUpdated: [name: string]
  fontSelected: [fontId: string]
  wordSpecimensUpdated: [updates: { moodboardItemId: string; url: string }[]]
}>()

const typefaceItems = computed(() =>
  props.images.filter((img) => img.isFontItem && img.fontName)
)

// Type sample ("Aa" word specimen) state
const typeSampleText = ref('Aa')
const typeSampleDraft = ref('')
const isEditingTypeSample = ref(false)
const generatingWordSpecimens = ref(false)
const typeSampleInputRef = ref<HTMLInputElement | null>(null)

function startEditTypeSample() {
  typeSampleDraft.value = typeSampleText.value === 'Aa' ? '' : typeSampleText.value
  isEditingTypeSample.value = true
  nextTick(() => typeSampleInputRef.value?.focus())
}

function cancelTypeSample() {
  isEditingTypeSample.value = false
}

async function commitTypeSample() {
  const word = typeSampleDraft.value.trim()
  isEditingTypeSample.value = false
  if (!word) return
  // Update immediately — watcher triggers canvas redraw via live FontFace rendering
  typeSampleText.value = word
  // Persist word to each font item's itemData so PSD export knows what text to use
  generatingWordSpecimens.value = true
  try {
    await Promise.all(
      typefaceItems.value.map((item) =>
        apiClient.updateMoodboardItem(props.moodboardId, item.moodboardItemId, {
          itemData: JSON.stringify({ fontId: item.fontId, word }),
        })
      )
    )
  } catch (err) {
    console.error(err)
  } finally {
    generatingWordSpecimens.value = false
  }
}

const message = useMessage()
const editingName = ref(false)
const editNameValue = ref('')
const nameInputRef = ref<{ focus: () => void } | null>(null)
const exportingPsd = ref(false)

const displayName = computed(() => props.moodboardName?.trim() || 'Moodboard')
/** Expose for modal header slot (avoids "not defined on instance" when slot is rendered in Card context). */
const isEditingName = computed(() => editingName.value)

function startEditName() {
  editNameValue.value = props.moodboardName ?? ''
  editingName.value = true
  nextTick(() => nameInputRef.value?.focus())
}

function cancelEditName() {
  editingName.value = false
  editNameValue.value = props.moodboardName ?? ''
}

async function saveName() {
  const trimmed = editNameValue.value.trim()
  editingName.value = false
  if (trimmed === (props.moodboardName ?? '')) return
  if (!trimmed) {
    editNameValue.value = props.moodboardName ?? ''
    return
  }
  try {
    await apiClient.updateMoodboard(props.moodboardId, { name: trimmed })
    emit('nameUpdated', trimmed)
    message.success('Board name updated')
  } catch (e) {
    message.error('Failed to update board name')
    editNameValue.value = props.moodboardName ?? ''
  }
}
const canvasRef = ref<HTMLCanvasElement | null>(null)
const canvasWrapRef = ref<HTMLElement | null>(null)
const selectedImageIndex = ref<number | null>(null)
const emphasisLevel = ref(0.5)
const refreshCount = ref(0)
const packingRandomizeTies = ref(false)
const packingRefreshVariation = ref(false)
const showCropModal = ref(false)

// ── Multi-page state ──
const activePage = ref(1)
const MAX_PAGES = 5
const totalPages = computed(() =>
  Math.max(1, new Set(props.images.map((img) => img.pageNumber ?? 1)).size)
)
const currentPageImages = computed(() =>
  props.images.filter((img) => (img.pageNumber ?? 1) === activePage.value)
)
const showMovePagePopup = ref(false)
const needsAutoSave = ref(false)
/** Holds crop just saved; used so layout uses new aspect before parent updates props. */
const lastSavedCropOverride = ref<{ moodboardItemId: string; crop: NormalizedCrop | null } | null>(null)

const BOARD_ASPECT = 7 / 5

/** Compute canvas dimensions to fit inside a given container with exact 7:5 or 5:7 aspect. */
function computeCanvasSizeToFitContainer(
  orient: 'horizontal' | 'vertical',
  containerW: number,
  containerH: number
): { width: number; height: number } {
  const aspect = orient === 'horizontal' ? BOARD_ASPECT : 1 / BOARD_ASPECT
  const maxW = Math.max(1, Math.floor(containerW))
  const maxH = Math.max(1, Math.floor(containerH))
  let width: number
  let height: number
  if (orient === 'horizontal') {
    width = Math.min(maxW, Math.round(maxH * aspect))
    height = Math.round(width / aspect)
  } else {
    height = Math.min(maxH, Math.round(maxW / aspect))
    width = Math.round(height * aspect)
  }
  return { width, height }
}

/** Compute canvas dimensions that fit in the modal viewport while keeping aspect exactly 7:5 or 5:7. */
function computeCanvasSizeForViewport(orient: 'horizontal' | 'vertical'): { width: number; height: number } {
  const aspect = orient === 'horizontal' ? BOARD_ASPECT : 1 / BOARD_ASPECT
  if (typeof window === 'undefined') {
    const short = 600
    return orient === 'horizontal'
      ? { width: Math.round(short * BOARD_ASPECT), height: short }
      : { width: short, height: Math.round(short * BOARD_ASPECT) }
  }
  const maxCardWidth = window.innerWidth * 0.98
  const horizontalPadding = 80 // card padding + canvas-wrap padding (16px each side)
  const verticalReserve = 120 // header, toolbar, canvas-wrap padding (16px top+bottom)
  const maxW = Math.max(300, maxCardWidth - horizontalPadding)
  const maxH = Math.max(300, window.innerHeight * 0.85 - verticalReserve)
  let width: number
  let height: number
  if (orient === 'horizontal') {
    width = Math.min(maxW, Math.round(maxH * aspect))
    height = Math.round(width / aspect)
  } else {
    height = Math.min(maxH, Math.round(maxW / aspect))
    width = Math.round(height * aspect)
  }
  return { width, height }
}

const orientation = ref<'horizontal' | 'vertical'>(
  (() => {
    const w = props.canvasWidth ?? 900
    const h = props.canvasHeight ?? 600
    if (!w || !h) return 'horizontal'
    return w >= h ? 'horizontal' : 'vertical'
  })()
)

const canvasWidth = ref(computeCanvasSizeForViewport(orientation.value).width)
const canvasHeight = ref(computeCanvasSizeForViewport(orientation.value).height)

watch(
  () => [props.canvasWidth, props.canvasHeight, props.show] as const,
  ([w, h, show]) => {
    if (!show) return
    const width = w ?? 900
    const height = h ?? 600
    const orient = width >= height ? 'horizontal' : 'vertical'
    orientation.value = orient
    // Use viewport-fitted dimensions initially; fit-to-container watch may refine when layout is ready
    const fitted = computeCanvasSizeForViewport(orient)
    canvasWidth.value = fitted.width
    canvasHeight.value = fitted.height
  },
  { immediate: true }
)

const isHorizontal = computed(() => orientation.value === 'horizontal')
const isVertical = computed(() => orientation.value === 'vertical')

async function setBoardOrientation(next: 'horizontal' | 'vertical') {
  if (orientation.value === next) return
  orientation.value = next
  // Fit to container when available to avoid scrollbars; else use viewport estimate
  await nextTick()
  const wrap = canvasWrapRef.value
  const containerW = wrap?.clientWidth ?? 0
  const containerH = wrap?.clientHeight ?? 0
  const { width, height } =
    containerW > 0 && containerH > 0
      ? computeCanvasSizeToFitContainer(next, containerW, containerH)
      : computeCanvasSizeForViewport(next)
  canvasWidth.value = width
  canvasHeight.value = height

  try {
    await apiClient.updateMoodboard(props.moodboardId, {
      canvasWidth: canvasWidth.value,
      canvasHeight: canvasHeight.value
    })
  } catch (err) {
    console.error(err)
    message.error('Failed to update board size')
  }

  await onRefresh()
}

const hasStoredLayout = computed(() =>
  currentPageImages.value.length > 0 && currentPageImages.value.every(
    (img) =>
      img.positionX != null && img.positionY != null && img.scale != null
  )
)

/** Effective dimensions for layout: use cropped aspect when crop exists. */
function getEffectiveDimensions(img: MoodboardBoardImage): { width: number; height: number } {
  const override = lastSavedCropOverride.value
  const crop =
    override?.moodboardItemId === img.moodboardItemId
      ? override.crop
      : img.cropLeft != null &&
          img.cropTop != null &&
          img.cropWidth != null &&
          img.cropHeight != null
        ? { cropWidth: img.cropWidth, cropHeight: img.cropHeight }
        : null
  const w = img.width ?? 1
  const h = img.height ?? 1
  if (crop) {
    return { width: w * crop.cropWidth, height: h * crop.cropHeight }
  }
  return { width: w, height: h }
}

const imagesForLayout = computed<ImageForLayout[]>(() =>
  currentPageImages.value.map((img) => {
    const { width, height } = getEffectiveDimensions(img)
    return {
      width,
      height,
      starred: img.starred,
      rating: img.rating ?? null,
      order: img.order
    }
  })
)

const layoutRects = ref<LayoutRect[]>([])

/** When modal is shown and layout is ready, size canvas to fit the actual container to avoid scrollbars. */
watch(
  () => [props.show, layoutRects.value.length] as const,
  ([show, rectCount]) => {
    if (!show || rectCount === 0) return
    nextTick(() => {
      const wrap = canvasWrapRef.value
      if (!wrap) return
      const w = wrap.clientWidth
      const h = wrap.clientHeight
      if (w <= 0 || h <= 0) return
      const orient = orientation.value
      const { width, height } = computeCanvasSizeToFitContainer(orient, w, h)
      canvasWidth.value = width
      canvasHeight.value = height
    })
  },
  { immediate: true }
)

/** Convert normalized rects (0-1) to pixel rects for canvas drawing. */
function normalizedToPixelRects(normalized: LayoutRect[]): LayoutRect[] {
  const cw = canvasWidth.value
  const scale = cw
  return normalized.map((r) => ({
    left: r.left * scale,
    top: r.top * scale,
    width: r.width * scale,
    height: r.height * scale
  }))
}

function computeLayout(options?: { forceAuto?: boolean }) {
  if (currentPageImages.value.length === 0) {
    layoutRects.value = []
    return
  }
  if (!options?.forceAuto && hasStoredLayout.value) {
    const cw = canvasWidth.value
    const ch = canvasHeight.value
    const minSide = Math.min(cw, ch)
    layoutRects.value = currentPageImages.value.map((img) => {
      const { width: effW, height: effH } = getEffectiveDimensions(img)
      const cx = (img.positionX ?? 0.5) * cw
      const cy = (img.positionY ?? 0.5) * ch
      const scale = img.scale ?? 1
      const displayShort = scale * minSide
      const aspect = effW && effH ? effW / effH : 1
      const w = aspect >= 1 ? displayShort * aspect : displayShort
      const h = aspect >= 1 ? displayShort : displayShort / aspect
      return { left: cx - w / 2, top: cy - h / 2, width: w, height: h }
    })
    return
  }
  const canvasAspect = canvasWidth.value / canvasHeight.value
  const normalized = computePackingLayout(canvasAspect, imagesForLayout.value, {
    randomizeTies: packingRandomizeTies.value,
    refreshVariation: packingRefreshVariation.value,
    selectedIndex: selectedImageIndex.value,
    emphasis: emphasisLevel.value,
    refreshStrength: Math.min(1, refreshCount.value * 0.15)
  })
  layoutRects.value = normalizedToPixelRects(normalized)
  packingRefreshVariation.value = false
}

// Drag state
const isDragging = ref(false)
const dragItemIndex = ref<number | null>(null)
const dragStartMouseX = ref(0)
const dragStartMouseY = ref(0)
const dragStartRectLeft = ref(0)
const dragStartRectTop = ref(0)

const selectedRect = computed(() => {
  const i = selectedImageIndex.value
  if (i === null) return null
  return layoutRects.value[i] ?? null
})

watch(
  () => [currentPageImages.value, props.show, canvasWidth.value, canvasHeight.value, selectedImageIndex.value] as const,
  () => {
    if (isDragging.value) return
    if (props.show && currentPageImages.value.length > 0) {
      computeLayout()
      if (needsAutoSave.value) {
        needsAutoSave.value = false
        nextTick(() => saveCurrentPageLayout())
      }
    }
  },
  { immediate: true }
)

// Reset selection and re-layout when switching pages
watch(activePage, () => {
  selectedImageIndex.value = null
  layoutRects.value = []
  showMovePagePopup.value = false
  computeLayout()
})

const selectedImageStarred = computed(() => {
  const i = selectedImageIndex.value
  return i !== null && currentPageImages.value[i]?.starred === true
})

const selectedImageIsFontItem = computed(() => {
  const i = selectedImageIndex.value
  return i !== null && currentPageImages.value[i]?.isFontItem === true
})

const loadedImages = ref<Map<string, HTMLImageElement>>(new Map())
const loadedFonts = ref<Set<string>>(new Set())

function getImageUrl(img: MoodboardBoardImage): string {
  const raw = img.previewUrl || img.iconUrl || img.url || ''
  if (!raw) return ''
  // Resolve relative URLs so canvas can load them (same-origin)
  if (raw.startsWith('/')) {
    return typeof window !== 'undefined' ? `${window.location.origin}${raw}` : raw
  }
  return raw
}

function loadAllImages() {
  // Clear image cache when loading a new set so we always fetch current URLs
  loadedImages.value = new Map()
  loadedFonts.value = new Set()
  if (props.images.length === 0) {
    draw()
    return
  }
  props.images.forEach((img) => {
    if (img.isFontItem) {
      // Load via FontFace API — no CORS taint issue for canvas text rendering
      const woff2 = img.woff2Url
      if (!woff2 || !img.fontId) {
        // No woff2 URL — mark ready so text renders with system font fallback
        if (img.fontId) loadedFonts.value = new Set([...loadedFonts.value, img.fontId])
        draw()
        return
      }
      const alias = `moodboard-font-${img.fontId}`
      const face = new FontFace(alias, `url("${woff2}")`)
      face.load().then((f) => {
        document.fonts.add(f)
        loadedFonts.value = new Set([...loadedFonts.value, img.fontId!])
        draw()
      }).catch(() => {
        // Font load failed — still mark ready so text renders with system font fallback
        loadedFonts.value = new Set([...loadedFonts.value, img.fontId!])
        draw()
      })
      return
    }
    const url = getImageUrl(img)
    if (!url) return
    const el = new Image()
    // Only use CORS mode for same-origin URLs. For external buckets (e.g. Spaces/S3)
    // that don't send Access-Control-Allow-Origin, setting crossOrigin triggers
    // noisy CORS errors even though <img> loading itself would work.
    if (typeof window !== 'undefined' && url.startsWith(window.location.origin)) {
      el.crossOrigin = 'anonymous'
    }
    el.onload = () => {
      loadedImages.value.set(img.id, el)
      draw()
    }
    el.onerror = () => {
      // On failure just draw placeholders for this image
      draw()
    }
    el.src = url
  })
  draw()
}

function draw() {
  const canvas = canvasRef.value
  if (!canvas || layoutRects.value.length === 0) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const cw = canvasWidth.value
  const ch = canvasHeight.value
  ctx.clearRect(0, 0, cw, ch)
  ctx.fillStyle = '#f5f5f5'
  ctx.fillRect(0, 0, cw, ch)

  // Clip to canvas (layout is scale-to-fit so no overflow; clip is a safety guard)
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, cw, ch)
  ctx.clip()

  const selected = selectedImageIndex.value
  currentPageImages.value.forEach((img, i) => {
    const rect = layoutRects.value[i]
    if (!rect) return

    // Font items: render live text using loaded FontFace
    if (img.isFontItem) {
      const word = typeSampleText.value || 'Aa'
      const fontAlias = `moodboard-font-${img.fontId}`
      ctx.save()
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(rect.left, rect.top, rect.width, rect.height)
      if (img.fontId && loadedFonts.value.has(img.fontId)) {
        // Start at max height, then scale down to fit 80% of width if needed
        let size = rect.height * 0.8
        ctx.font = `${size}px "${fontAlias}"`
        const measuredWidth = ctx.measureText(word).width
        if (measuredWidth > rect.width * 0.8) {
          size = size * (rect.width * 0.8) / measuredWidth
        }
        ctx.font = `${size}px "${fontAlias}"`
        ctx.fillStyle = '#1a1a1a'
        ctx.textBaseline = 'middle'
        ctx.textAlign = 'center'
        ctx.fillText(word, rect.left + rect.width / 2, rect.top + rect.height / 2)
      }
      ctx.restore()
      return
    }

    const imageEl = loadedImages.value.get(img.id)
    if (!imageEl) {
      ctx.fillStyle = '#e0e0e0'
      ctx.fillRect(rect.left, rect.top, rect.width, rect.height)
      return
    }
    const srcW = imageEl.naturalWidth || 1
    const srcH = imageEl.naturalHeight || 1
    const cl = img.cropLeft ?? 0
    const ct = img.cropTop ?? 0
    const cw_ = img.cropWidth ?? 1
    const ch_ = img.cropHeight ?? 1
    if (img.cropLeft != null && img.cropTop != null && img.cropWidth != null && img.cropHeight != null) {
      ctx.drawImage(
        imageEl,
        cl * srcW, ct * srcH, cw_ * srcW, ch_ * srcH,
        rect.left, rect.top, rect.width, rect.height
      )
    } else {
      ctx.drawImage(imageEl, rect.left, rect.top, rect.width, rect.height)
    }
  })

  if (selected !== null) {
    const r = layoutRects.value[selected]
    if (r) {
      ctx.strokeStyle = '#18a058'
      ctx.lineWidth = 3
      ctx.strokeRect(r.left - 2, r.top - 2, r.width + 4, r.height + 4)
    }
  }
  ctx.restore()
}

// Redraw when layout, loaded images/fonts, selection, word specimen text, or image data changes
watch([layoutRects, loadedImages, loadedFonts, selectedImageIndex, typeSampleText, () => props.images], draw, { deep: true })
onMounted(() => {
  loadAllImages()
  window.addEventListener('mouseup', onCanvasMouseUp)
})

onUnmounted(() => {
  window.removeEventListener('mouseup', onCanvasMouseUp)
  if (saveDebounceTimer) clearTimeout(saveDebounceTimer)
})
watch(() => props.images, () => loadAllImages(), { deep: true })
// When modal opens with images, ensure we load (in case images were set before show)
watch(() => props.show, (show) => {
  if (show && props.images.length > 0) loadAllImages()
})

function hitTest(clientX: number, clientY: number): number | null {
  const canvas = canvasRef.value
  if (!canvas) return null
  const rect = canvas.getBoundingClientRect()
  const scaleX = canvas.width / rect.width
  const scaleY = canvas.height / rect.height
  const x = (clientX - rect.left) * scaleX
  const y = (clientY - rect.top) * scaleY
  for (let i = layoutRects.value.length - 1; i >= 0; i--) {
    const r = layoutRects.value[i]
    if (x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height) return i
  }
  return null
}

function onCanvasMouseDown(e: MouseEvent) {
  showMovePagePopup.value = false
  const idx = hitTest(e.clientX, e.clientY)
  selectedImageIndex.value = idx
  if (idx === null) return
  isDragging.value = true
  dragItemIndex.value = idx
  dragStartMouseX.value = e.clientX
  dragStartMouseY.value = e.clientY
  dragStartRectLeft.value = layoutRects.value[idx].left
  dragStartRectTop.value = layoutRects.value[idx].top
}

function onCanvasMouseMove(e: MouseEvent) {
  if (!isDragging.value || dragItemIndex.value === null) return
  const dx = e.clientX - dragStartMouseX.value
  const dy = e.clientY - dragStartMouseY.value
  const rect = layoutRects.value[dragItemIndex.value]
  layoutRects.value[dragItemIndex.value] = {
    ...rect,
    left: dragStartRectLeft.value + dx,
    top: dragStartRectTop.value + dy,
  }
}

async function onCanvasMouseUp() {
  if (!isDragging.value || dragItemIndex.value === null) return
  isDragging.value = false
  const idx = dragItemIndex.value
  dragItemIndex.value = null
  await saveItemPosition(idx)
}

let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null
function debouncedSavePosition(idx: number) {
  if (saveDebounceTimer) clearTimeout(saveDebounceTimer)
  saveDebounceTimer = setTimeout(() => { void saveItemPosition(idx) }, 300)
}

function onCanvasWheel(e: WheelEvent) {
  const i = selectedImageIndex.value
  if (i === null) return
  const rect = layoutRects.value[i]
  const factor = e.deltaY < 0 ? 1.08 : 0.93
  const newW = rect.width * factor
  const newH = rect.height * factor
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  layoutRects.value[i] = {
    left: cx - newW / 2,
    top: cy - newH / 2,
    width: newW,
    height: newH,
  }
  debouncedSavePosition(i)
}

async function saveItemPosition(idx: number) {
  const img = currentPageImages.value[idx]
  const rect = layoutRects.value[idx]
  if (!img || !rect) return
  const [ps] = rectsToPositionScale(canvasWidth.value, canvasHeight.value, [rect], [imagesForLayout.value[idx]])
  await apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
    positionX: Math.max(0, Math.min(1, ps.positionX)),
    positionY: Math.max(0, Math.min(1, ps.positionY)),
    scale: Math.max(0.01, Math.min(10, ps.scale)),
  })
}

async function onDelete() {
  const i = selectedImageIndex.value
  if (i === null) return
  const img = currentPageImages.value[i]
  try {
    await apiClient.deleteMoodboardItem(props.moodboardId, img.moodboardItemId)
    message.success('Removed from moodboard')
    emit('deleted', img.moodboardItemId)
    selectedImageIndex.value = null
  } catch (err) {
    console.error(err)
    message.error('Failed to remove')
  }
}

async function onStar() {
  const i = selectedImageIndex.value
  if (i === null) return
  const img = currentPageImages.value[i]
  const nextStarred = !img.starred
  let itemData: { imageId: string; starred: boolean; [k: string]: unknown }
  try {
    const parsed =
      typeof img.itemData === 'string'
        ? JSON.parse(img.itemData || '{}')
        : (img.itemData && typeof img.itemData === 'object' ? (img.itemData as Record<string, unknown>) : {})
    itemData = { ...parsed, imageId: img.id, starred: nextStarred }
  } catch {
    itemData = { imageId: img.id, starred: nextStarred }
  }
  try {
    await apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
      itemData: JSON.stringify(itemData)
    })
    message.success(nextStarred ? 'Starred' : 'Unstarred')
    emit('layoutSaved')
  } catch (err) {
    console.error(err)
    message.error('Failed to update')
  }
}

async function saveCurrentPageLayout() {
  const rects = layoutRects.value
  if (rects.length === 0) return
  const positionScale = rectsToPositionScale(canvasWidth.value, canvasHeight.value, rects, imagesForLayout.value)
  await Promise.all(
    currentPageImages.value.map((img, idx) => {
      const ps = positionScale[idx]
      return apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
        positionX: Math.max(0, Math.min(1, ps.positionX)),
        positionY: Math.max(0, Math.min(1, ps.positionY)),
        scale: Math.max(0.01, Math.min(10, ps.scale))
      })
    })
  )
  emit('layoutSaved')
}

async function onRefresh() {
  refreshCount.value++
  packingRandomizeTies.value = true
  packingRefreshVariation.value = true
  computeLayout({ forceAuto: true })
  await nextTick()
  if (layoutRects.value.length === 0) return
  try {
    await saveCurrentPageLayout()
    message.success('Layout updated')
  } catch (err) {
    console.error('[Moodboard Refresh] save failed', err)
    message.error('Failed to save layout')
  }
}

async function onExportPsd() {
  if (props.images.length === 0) return
  exportingPsd.value = true
  try {
    const { blob, filename } = await apiClient.exportMoodboardPsd(props.moodboardId)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    message.success('PSD downloaded')
  } catch (err) {
    console.error(err)
    message.error('Failed to export PSD')
  } finally {
    exportingPsd.value = false
  }
}

const cropImageUrl = computed(() => {
  const i = selectedImageIndex.value
  if (i === null) return ''
  return getImageUrl(currentPageImages.value[i])
})
const cropImageWidth = computed(() => {
  const i = selectedImageIndex.value
  return i !== null ? (currentPageImages.value[i]?.width ?? 0) : 0
})
const cropImageHeight = computed(() => {
  const i = selectedImageIndex.value
  return i !== null ? (currentPageImages.value[i]?.height ?? 0) : 0
})
const cropInitialCrop = computed(() => {
  const i = selectedImageIndex.value
  if (i === null) return null
  const img = currentPageImages.value[i]
  if (img.cropLeft == null || img.cropTop == null || img.cropWidth == null || img.cropHeight == null) return null
  return {
    cropLeft: img.cropLeft,
    cropTop: img.cropTop,
    cropWidth: img.cropWidth,
    cropHeight: img.cropHeight
  }
})

function onCrop() {
  if (selectedImageIndex.value === null) return
  showCropModal.value = true
}

function clampCrop01(n: number): number {
  return Math.max(0, Math.min(1, n))
}

async function onCropConfirm(crop: NormalizedCrop) {
  const i = selectedImageIndex.value
  if (i === null) return
  const img = currentPageImages.value[i]
  const clamped = {
    cropLeft: clampCrop01(crop.cropLeft),
    cropTop: clampCrop01(crop.cropTop),
    cropWidth: clampCrop01(crop.cropWidth),
    cropHeight: clampCrop01(crop.cropHeight)
  }
  try {
    await apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, clamped)
    message.success('Crop applied')
    emit('cropUpdated', { moodboardItemId: img.moodboardItemId, crop: clamped })
    lastSavedCropOverride.value = { moodboardItemId: img.moodboardItemId, crop: clamped }
    try {
      await onRefresh()
    } finally {
      lastSavedCropOverride.value = null
    }
  } catch (err) {
    console.error(err)
    message.error('Failed to apply crop')
  }
}

async function onCropClear() {
  const i = selectedImageIndex.value
  if (i === null) return
  const img = currentPageImages.value[i]
  try {
    await apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
      cropLeft: null,
      cropTop: null,
      cropWidth: null,
      cropHeight: null
    })
    message.success('Crop cleared')
    emit('cropUpdated', { moodboardItemId: img.moodboardItemId, crop: null })
    lastSavedCropOverride.value = { moodboardItemId: img.moodboardItemId, crop: null }
    try {
      await onRefresh()
    } finally {
      lastSavedCropOverride.value = null
    }
  } catch (err) {
    console.error(err)
    message.error('Failed to clear crop')
  }
}

function onClose() {
  editingName.value = false
  selectedImageIndex.value = null
  emphasisLevel.value = 0.5
  refreshCount.value = 0
  packingRandomizeTies.value = false
  packingRefreshVariation.value = false
  lastSavedCropOverride.value = null
  loadedImages.value.clear()
  typeSampleText.value = 'Aa'
  isEditingTypeSample.value = false
  generatingWordSpecimens.value = false
  activePage.value = 1
  showMovePagePopup.value = false
  emit('closed')
}

// ── Add Page ──
async function onAddPage() {
  if (totalPages.value >= MAX_PAGES) return
  const newTotal = totalPages.value + 1
  const nonFontImages = [...props.images]
    .filter((img) => !img.isFontItem)
    .sort((a, b) => ((a.pageNumber ?? 1) - (b.pageNumber ?? 1)) || ((a.order ?? 0) - (b.order ?? 0)))
  const pageSize = Math.ceil(nonFontImages.length / newTotal)
  const changed = nonFontImages
    .map((img, i) => ({ img, newPage: Math.min(Math.floor(i / pageSize) + 1, newTotal) }))
    .filter(({ img, newPage }) => (img.pageNumber ?? 1) !== newPage)
  try {
    await Promise.all(
      changed.map(({ img, newPage }) =>
        apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
          pageNumber: newPage,
          positionX: null,
          positionY: null,
          scale: null
        })
      )
    )
    // Switch page only after parent reloads props.images with the new page numbers
    const unwatch = watch(totalPages, (val) => {
      if (val >= newTotal) {
        activePage.value = newTotal
        unwatch()
      }
    })
    emit('layoutSaved')
  } catch (err) {
    console.error(err)
    message.error('Failed to add page')
  }
}

// ── Move Page ──
function onMovePageClick() {
  if (totalPages.value === 2) {
    onMovePage(activePage.value === 1 ? 2 : 1)
  } else {
    showMovePagePopup.value = !showMovePagePopup.value
  }
}

async function onMovePage(targetPage: number) {
  const i = selectedImageIndex.value
  if (i === null) return
  const img = currentPageImages.value[i]
  showMovePagePopup.value = false
  try {
    await apiClient.updateMoodboardItem(props.moodboardId, img.moodboardItemId, {
      pageNumber: targetPage,
      positionX: null,
      positionY: null,
      scale: null
    })
    selectedImageIndex.value = null
    needsAutoSave.value = true
    emit('layoutSaved')
    message.success(`Moved to page ${targetPage}`)
  } catch (err) {
    console.error(err)
    message.error('Failed to move image')
  }
}

// ── Page thumbnails ──
const thumbCanvasRefs = ref(new Map<number, HTMLCanvasElement>())

function setThumbCanvasRef(page: number, el: HTMLCanvasElement | null) {
  if (el) thumbCanvasRefs.value.set(page, el)
  else thumbCanvasRefs.value.delete(page)
}

function drawPageThumbnail(pageNum: number) {
  const tc = thumbCanvasRefs.value.get(pageNum)
  if (!tc) return
  const ctx = tc.getContext('2d')
  if (!ctx) return
  const W = 112, H = 80
  ctx.clearRect(0, 0, W, H)
  ctx.fillStyle = '#2a2a2a'
  ctx.fillRect(0, 0, W, H)
  const pageImgs = props.images.filter((img) => (img.pageNumber ?? 1) === pageNum && !img.isFontItem)
  const cw = canvasWidth.value, ch = canvasHeight.value
  for (const img of pageImgs) {
    if (img.positionX == null || img.positionY == null || img.scale == null) continue
    const minSide = Math.min(cw, ch)
    const { width: effW, height: effH } = getEffectiveDimensions(img)
    const aspect = effW && effH ? effW / effH : 1
    const displayShort = img.scale * minSide * (W / cw)
    const w = aspect >= 1 ? displayShort * aspect : displayShort
    const h = aspect >= 1 ? displayShort : displayShort / aspect
    const cx = img.positionX * W
    const cy = img.positionY * H
    const imageEl = loadedImages.value.get(img.id)
    if (!imageEl) {
      ctx.fillStyle = '#555'
      ctx.fillRect(cx - w / 2, cy - h / 2, w, h)
      continue
    }
    const srcW = imageEl.naturalWidth, srcH = imageEl.naturalHeight
    const cl = img.cropLeft ?? 0, ct = img.cropTop ?? 0
    const cw_ = img.cropWidth ?? 1, ch_ = img.cropHeight ?? 1
    ctx.drawImage(imageEl, cl * srcW, ct * srcH, cw_ * srcW, ch_ * srcH, cx - w / 2, cy - h / 2, w, h)
  }
}

watch([() => props.images, loadedImages], () => {
  nextTick(() => {
    for (let p = 1; p <= totalPages.value; p++) drawPageThumbnail(p)
  })
}, { deep: true })
</script>

<style scoped>
.moodboard-modal-header {
  min-width: 0;
}
.moodboard-name-clickable {
  cursor: pointer;
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  outline: none;
}
.moodboard-name-clickable:hover {
  background: var(--n-color-hover);
}
.moodboard-name-clickable:focus-visible {
  box-shadow: 0 0 0 2px var(--n-color-primary);
}
.moodboard-board-modal {
  min-height: 400px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.toolbar-left {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  margin-left: auto;
}
.aspect-label {
  color: #888;
}
.aspect-note {
  color: #888;
}
.empty-canvas,
.loading-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px;
}
.canvas-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  max-height: 85vh;
  padding: 16px;
  background: #1e1e1e;
  border-radius: 8px;
}
.canvas-inner {
  position: relative;
  flex-shrink: 0;
}
.canvas-wrap canvas {
  display: block;
  cursor: grab;
}
.canvas-wrap canvas.is-dragging {
  cursor: grabbing;
}
.image-action-overlay {
  position: absolute;
  display: flex;
  gap: 4px;
  background: rgba(30, 30, 30, 0.85);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  padding: 3px 6px;
  pointer-events: auto;
  z-index: 10;
  white-space: nowrap;
}
.typefaces-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.typefaces-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 4px;
  white-space: nowrap;
}
.typeface-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.typeface-chip:hover {
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.5);
  color: #a5b4fc;
}
.type-sample-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 3px 8px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-style: italic;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
}
.type-sample-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.5);
  color: #a5b4fc;
}
.type-sample-btn.active {
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.4);
  color: #c7d2fe;
  font-style: normal;
}
.type-sample-btn.loading {
  opacity: 0.6;
  cursor: wait;
}
.type-sample-spinner {
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: rgba(255,255,255,0.7);
  border-radius: 50%;
  animation: tspin 0.7s linear infinite;
}
@keyframes tspin { to { transform: rotate(360deg); } }
.type-sample-input {
  width: 120px;
  padding: 2px 8px;
  border-radius: 100px;
  border: 1px solid rgba(99, 102, 241, 0.6);
  background: rgba(99, 102, 241, 0.1);
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  outline: none;
}
.type-sample-input::placeholder {
  color: rgba(255,255,255,0.3);
}
.board-and-pages {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.pages-sidebar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 120px;
  flex-shrink: 0;
  padding-top: 4px;
}
.page-thumb {
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid transparent;
  overflow: hidden;
  position: relative;
  background: #2a2a2a;
  transition: border-color 0.15s;
}
.page-thumb:hover {
  border-color: rgba(255,255,255,0.3);
}
.page-thumb.active {
  border-color: #18a058;
}
.page-thumb-canvas {
  display: block;
  width: 112px;
  height: 80px;
}
.page-thumb-label {
  position: absolute;
  bottom: 3px;
  right: 5px;
  font-size: 10px;
  color: rgba(255,255,255,0.7);
  background: rgba(0,0,0,0.4);
  border-radius: 3px;
  padding: 0 3px;
}
.move-page-popup {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: rgba(30,30,30,0.95);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 20;
  min-width: 80px;
}
</style>
